// Rest action modal — opened when the player clicks a rest-action button
// in the prep-phase banner. White screen styled to match the
// weapon-mastery / spell choosers in character creation; reuses
// <weapon-option-card> for the weapon-mastery flow so the UX is identical
// to what the player saw at character creation.

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import '../shared/weapon-option-card.js';
import '../shared/spell-option-card.js';

/**
 * @typedef {import('../../types/jsdoc-types.js').RestAction} RestAction
 * @typedef {import('../../types/jsdoc-types.js').RestActionOption} RestActionOption
 */

const REST_ACTION_TYPE_WEAPON_MASTERY = 'rest:change-weapon-mastery';
const REST_ACTION_TYPE_PREPARED_SPELLS = 'rest:change-prepared-spells';

class RestActionModal extends BaseComponent {
  static properties = {
    action: { type: Object },
    _selections: { type: Array, state: true },
    _submitting: { type: Boolean, state: true },
  };

  static styles = [
    css`
      :host {
        display: block;
      }

      .backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.45);
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding-top: 60px;
        z-index: 10001;
        pointer-events: auto;
      }

      .modal-screen {
        background: #ffffff;
        color: #212529;
        border-radius: 8px;
        width: min(960px, 95vw);
        max-height: calc(100vh - 100px);
        display: flex;
        flex-direction: column;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 24px;
        border-bottom: 1px solid #dee2e6;
        flex-shrink: 0;
      }

      .header h3 {
        margin: 0;
        font-size: 22px;
        font-weight: 600;
      }

      .close-btn {
        background: none;
        border: none;
        color: #495057;
        font-size: 28px;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background 0.2s;
      }

      .close-btn:hover {
        background: #f1f3f5;
      }

      .content {
        padding: 16px 24px;
        overflow-y: auto;
        flex: 1 1 auto;
        min-height: 0;
      }

      .description {
        font-size: 14px;
        color: #495057;
        margin-bottom: 8px;
      }

      .counter {
        font-size: 13px;
        color: #6c757d;
        margin-bottom: 16px;
      }

      .step-heading {
        font-size: 15px;
        font-weight: 600;
        color: #343a40;
        margin: 0 0 6px 0;
      }

      .step-subtext {
        font-size: 13px;
        color: #6c757d;
        margin-bottom: 8px;
      }

      .step-section + .step-section {
        margin-top: 20px;
      }

      .empty-step {
        font-size: 13px;
        color: #6c757d;
        font-style: italic;
        padding: 8px 0;
      }

      .footer {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        padding: 12px 24px;
        border-top: 1px solid #dee2e6;
        flex-shrink: 0;
      }
    `,
  ];

  constructor() {
    super();
    /** @type {RestAction|null} */
    this.action = null;
    /** @type {string[]} */
    this._selections = [];
    this._submitting = false;
  }

  async connectedCallback() {
    await super.connectedCallback();
    // Bootstrap tooltips are appended to <body> with z-index: 1080. The
    // modal scrim (.backdrop) sits at 10001; without this lift, tooltips
    // from <spell-option-card> / <weapon-option-card> render behind it.
    if (!document.querySelector('#rest-action-modal-tooltip-z')) {
      const style = document.createElement('style');
      style.id = 'rest-action-modal-tooltip-z';
      style.textContent = '.tooltip { z-index: 10100 !important; }';
      document.head.appendChild(style);
    }
  }

  willUpdate(changed) {
    if (changed.has('action') && this.action) {
      this._selections = [...(this.action.currentSelections || [])];
    }
  }

  /** @param {RestActionOption} option */
  _isSelected(option) {
    return this._selections.includes(option.id);
  }

  /** @param {RestActionOption} option */
  _isDisabled(option) {
    if (!this.action) return false;
    if (option.disabled) return true;
    const selected = this._isSelected(option);
    const atCap = !selected && this._selections.length >= this.action.count;
    if (this.action.replaceLimit != null) {
      const original = new Set(this.action.currentSelections || []);
      const newPicks = this._selections.filter((s) => !original.has(s)).length;
      const wouldBeNew = !selected && !original.has(option.id);
      if (wouldBeNew && newPicks >= this.action.replaceLimit) return true;
    }
    return atCap;
  }

  /** @param {RestActionOption} option */
  _toggle(option) {
    if (this._isDisabled(option) && !this._isSelected(option)) return;
    if (this._isSelected(option)) {
      this._selections = this._selections.filter((s) => s !== option.id);
    } else {
      this._selections = [...this._selections, option.id];
    }
  }

  _isDirty() {
    if (!this.action) return false;
    const original = [...(this.action.currentSelections || [])].sort();
    const current = [...this._selections].sort();
    if (original.length !== current.length) return true;
    return original.some((id, i) => id !== current[i]);
  }

  _handleSave() {
    if (!this.action || this._submitting) return;
    this._submitting = true;
    this.dispatchEvent(
      new CustomEvent('rest-action-submit', {
        detail: {
          type: this.action.type,
          characterId: this.action.characterId,
          selections: this._selections,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _handleCancel() {
    this.dispatchEvent(new CustomEvent('rest-action-cancel', { bubbles: true, composed: true }));
  }

  /**
   * Per-type option renderer. Both branches keep `recommended` off — the
   * rest-action picker is for an experienced PC re-tuning their existing
   * choices, not a new player picking from scratch.
   * @param {RestActionOption} opt
   * @param {{ checked: boolean, disabled: boolean }} state
   */
  _renderOption(opt, state) {
    if (this.action?.type === REST_ACTION_TYPE_WEAPON_MASTERY) {
      return this._renderWeaponOption(opt, state);
    }
    if (this.action?.type === REST_ACTION_TYPE_PREPARED_SPELLS) {
      return this._renderSpellOption(opt, state);
    }
    return this._renderGenericOption(opt, state);
  }

  /**
   * @param {RestActionOption} opt
   * @param {{ checked: boolean, disabled: boolean }} state
   */
  _renderWeaponOption(opt, state) {
    const masteryProperty = opt.meta?.masteryProp || '';
    return html`
      <div class="col-12 col-md-6 col-lg-4">
        <weapon-option-card
          name="${opt.label}"
          slug="${opt.id}"
          mastery-property="${masteryProperty}"
          ?selected="${state.checked}"
          ?disabled="${state.disabled}"
          input-type="checkbox"
          @card-selected="${(/** @type {CustomEvent} */ e) => {
            const target = this.action?.options.find((o) => o.id === e.detail.slug);
            if (target) this._toggle(target);
          }}"
        ></weapon-option-card>
      </div>
    `;
  }

  /**
   * @param {RestActionOption} opt
   * @param {{ checked: boolean, disabled: boolean }} state
   */
  _renderSpellOption(opt, state) {
    const meta = opt.meta || {};
    const school = typeof meta.spellSchool === 'string' ? meta.spellSchool : '';
    const description = typeof meta.spellDescription === 'string' ? meta.spellDescription : '';
    const concentration = !!meta.concentration;
    return html`
      <div class="col-12 col-md-6 col-lg-4">
        <spell-option-card
          name="${opt.label}"
          slug="${opt.id}"
          school="${school}"
          ?concentration="${concentration}"
          description="${description}"
          ?selected="${state.checked}"
          ?disabled="${state.disabled}"
          input-type="checkbox"
          @card-selected="${(/** @type {CustomEvent} */ e) => {
            const target = this.action?.options.find((o) => o.id === e.detail.slug);
            if (target) this._toggle(target);
          }}"
        ></spell-option-card>
      </div>
    `;
  }

  /**
   * @param {RestActionOption} opt
   * @param {{ checked: boolean, disabled: boolean }} state
   */
  _renderGenericOption(opt, state) {
    const meta = opt.meta || {};
    const metaParts = [];
    if (meta.spellLevel != null) metaParts.push(`Level ${meta.spellLevel}`);
    if (meta.spellSchool) metaParts.push(meta.spellSchool);
    if (meta.castingTime) metaParts.push(meta.castingTime);
    return html`
      <div class="col-12 col-md-6">
        <label
          class="card p-2 d-flex flex-row align-items-start gap-2 ${state.checked ? 'border-primary' : ''}"
          style="${state.disabled ? 'opacity: 0.5; cursor: not-allowed;' : 'cursor: pointer;'}"
        >
          <input
            type="checkbox"
            class="form-check-input mt-1"
            .checked=${state.checked}
            ?disabled=${state.disabled}
            @change=${() => this._toggle(opt)}
          />
          <div class="flex-grow-1">
            <div class="fw-medium">${opt.label}</div>
            ${metaParts.length > 0 ? html`<div class="small text-muted">${metaParts.join(' · ')}</div>` : ''}
          </div>
        </label>
      </div>
    `;
  }

  render() {
    if (!this.action) return html``;
    const isReplaceN = this.action.replaceLimit != null && this.action.replaceLimit > 0;
    const inner = isReplaceN ? this._renderTwoStep() : this._renderSingleList();

    return html`
      <div class="backdrop" @click="${this._handleCancel}">
        <div class="modal-screen" @click="${(/** @type {Event} */ e) => e.stopPropagation()}">
          <div class="header">
            <h3>${this.action.label}</h3>
            <button class="close-btn" @click="${this._handleCancel}" aria-label="Close">&times;</button>
          </div>
          <div class="content">
            ${this.action.description ? html`<div class="description">${this.action.description}</div>` : ''}
            ${inner}
          </div>
          <div class="footer">
            <button class="btn btn-outline-secondary" @click="${this._handleCancel}">Cancel</button>
            <button
              class="btn btn-primary"
              ?disabled="${this._isSaveDisabled()}"
              @click="${this._handleSave}"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    `;
  }

  _isSaveDisabled() {
    if (!this.action || this._submitting || !this._isDirty()) return true;
    if (this._selections.length > this.action.count) return true;
    // Two-step replace path: every removed mastery must be matched by a
    // replacement before save unlocks (size-preserving swap).
    if (this.action.replaceLimit != null && this.action.replaceLimit > 0) {
      const original = this.action.currentSelections || [];
      const deselected = original.filter((s) => !this._selections.includes(s));
      const newPicks = this._selections.filter((s) => !original.includes(s));
      if (newPicks.length !== deselected.length) return true;
    }
    return false;
  }

  /**
   * Replace-N flow (e.g., Fighter weapon mastery, replaceLimit > 0):
   * Step 1 — uncheck up to `replaceLimit` of the current selections.
   * Step 2 — choose the same number of replacements from the full eligible
   * list; deselected items appear but are disabled so the player can't
   * just re-pick what they removed.
   */
  _renderTwoStep() {
    if (!this.action) return html``;
    const original = this.action.currentSelections || [];
    const replaceLimit = /** @type {number} */ (this.action.replaceLimit);
    const kept = original.filter((s) => this._selections.includes(s));
    const deselected = original.filter((s) => !this._selections.includes(s));
    const newPicks = this._selections.filter((s) => !original.includes(s));
    const canDeselectMore = deselected.length < replaceLimit;

    // Step 1 lists the original selections; unchecking them moves them into
    // `deselected`. Once the deselect budget (replaceLimit) is full, the
    // remaining checked items lock to prevent over-removal.
    const step1Options = this.action.options.filter((o) => original.includes(o.id));
    // Step 2 lists every eligible weapon, including the still-kept ones
    // (shown checked + disabled — the player drives keep/remove from
    // step 1) and the ones deselected in step 1 (shown disabled so the
    // swap is visible but they can't re-pick them).
    const step2Options = this.action.options;
    const replacementsRemaining = deselected.length - newPicks.length;

    return html`
      <div class="step-section">
        <h5 class="step-heading">
          Step 1 — Remove up to ${replaceLimit}
          ${replaceLimit === 1 ? 'mastery' : 'masteries'}
        </h5>
        <div class="step-subtext">${deselected.length} / ${replaceLimit} removed</div>
        ${
          step1Options.length === 0
            ? html`<div class="empty-step">You don't have any current masteries to remove.</div>`
            : html`<div class="row g-2">
              ${step1Options.map((o) => {
                const checked = kept.includes(o.id);
                // When the deselect budget is full, lock checked items so
                // the player can't deselect more. Already-deselected items
                // remain interactive (re-checking returns them to kept).
                const disabled = checked && !canDeselectMore;
                return this._renderOption(o, { checked, disabled });
              })}
            </div>`
        }
      </div>

      <div class="step-section">
        <h5 class="step-heading">
          Step 2 — Pick ${deselected.length}
          replacement${deselected.length === 1 ? '' : 's'}
        </h5>
        <div class="step-subtext">${newPicks.length} / ${deselected.length} picked</div>
        <div class="row g-2">
          ${step2Options.map((o) => {
            const isKept = kept.includes(o.id);
            const isDeselected = deselected.includes(o.id);
            const isPicked = newPicks.includes(o.id);
            // Picks are gated on having deselected something in step 1.
            // Until then `replacementsRemaining` is 0 and every unpicked
            // option disables itself — the list still renders so the
            // player can preview the full set of eligible replacements.
            const noBudget = !isPicked && !isKept && replacementsRemaining <= 0;
            return this._renderOption(o, {
              // Kept items appear in step 2 too, checked + locked: they're
              // still on the mastery list, but step 1 owns the toggle.
              checked: isKept || isPicked,
              disabled: isKept || isDeselected || noBudget,
            });
          })}
        </div>
      </div>
    `;
  }

  /**
   * Single-list flow (replaceLimit == null): one card-grid of every eligible
   * option. Deselect existing + pick new ones freely up to `count`.
   * The per-option renderer dispatches by action type — weapon-mastery
   * uses `<weapon-option-card>`, prepared-spells uses `<spell-option-card>`,
   * future types fall back to a generic checkbox card.
   */
  _renderSingleList() {
    if (!this.action) return html``;
    const counterText = `${this._selections.length} / ${this.action.count}`;
    return html`
      <div class="counter">${counterText}</div>
      <div class="row g-2">
        ${this.action.options.map((o) => {
          const checked = this._isSelected(o);
          const disabled = this._isDisabled(o) && !checked;
          return this._renderOption(o, { checked, disabled });
        })}
      </div>
    `;
  }
}

customElements.define('rest-action-modal', RestActionModal);
